import { useNavigate, Link } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { OfferLineTemplatesTable } from "@/components/admin/OfferLineTemplatesTable";
import { OfferTemplatesAccordion } from "@/components/admin/OfferTemplatesAccordion";
import { useEffect } from "react";

/**
 * /innstillinger/produktkatalog — admin-side for å redigere
 * produktlinjer (offer_line_templates) og tilbudsmaler (offer_templates
 * + offer_template_lines). Endringer slår igjennom umiddelbart for
 * NYE tilbud; eksisterende offer_lines er kopier og berøres ikke.
 */
export default function ProductCatalogPage() {
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();
  const isAdmin =
    activeUser?.role === "admin" || activeUser?.role === "superadmin";

  // Beskytt mot direkte navigasjon for ikke-admin
  useEffect(() => {
    if (activeUser && !isAdmin) navigate("/innstillinger");
  }, [activeUser, isAdmin, navigate]);

  if (!isAdmin) return null;

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/innstillinger">
            <SrIcon name="arrow-left" className="w-3.5 h-3.5 mr-1" />
            Tilbake til innstillinger
          </Link>
        </Button>
      </div>

      <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2 mb-2">
        <SrIcon name="database" className="h-6 w-6" />
        Produktkatalog
      </h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Rediger produktlinjer og tilbudsmaler som brukes i tilbudsgeneratoren.
        Endringer påvirker bare NYE tilbud — eksisterende tilbud er kopier
        og berøres ikke.
      </p>

      <Card>
        <CardContent className="p-4">
          <Tabs defaultValue="lines">
            <TabsList>
              <TabsTrigger value="lines">Produktlinjer</TabsTrigger>
              <TabsTrigger value="templates">Tilbudsmaler</TabsTrigger>
            </TabsList>

            <TabsContent value="lines" className="mt-4">
              <OfferLineTemplatesTable />
            </TabsContent>

            <TabsContent value="templates" className="mt-4">
              <OfferTemplatesAccordion />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
